import { RecordManagement } from "app/class/record-management";
import { authService } from "app/services/auth/authentication";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import attachmentApi from "app/services/http-services/attachment/attachment-api";
import recordApi, { platformPackageOperationRequestConfig } from "app/services/http-services/record/record-api";
import sessionApi from "app/services/http-services/session/session-api";
import { ReferenceMode } from "app/types/record-management-types";
import { NavigateFunction } from "react-router-dom";
import { GateApiScope, GateNavigatePayload } from "modules/gate/types/gate";
import { clearRememberedLogin, getRememberedLogin, saveRememberedLogin } from "./rememberedLoginStorage";
import { emitRecordMutation } from "shared/services/recordMutationBus";
import queryClient from "app/config/queryClient";
import queryKeys from "shared/services/queryKeys";
import { buildPublicLoginPath, fetchLoginGatePage, getSafeReturnTo } from "app/services/auth/loginRedirect";
import i18n from "app/config/i18n";

interface GateHostContext {
    navigate?: NavigateFunction;
    notify?: (message: string, level?: string) => void;
    basePath?: string;
    // Gate page locale (session-driven). The host app's i18next switches only after its own
    // bundle resolves, so record reads triggered by gate:locale-changed must not rely on it.
    locale?: string;
}

const recordLanguage = (context?: GateHostContext) => context?.locale || i18n.resolvedLanguage || i18n.language;

type HostRegistry = Record<GateApiScope, (payload?: any, context?: GateHostContext) => Promise<unknown> | unknown>;

type GateFilePayload = {
    __gateFile?: true;
    name?: string;
    type?: string;
    lastModified?: number;
    buffer?: ArrayBuffer;
};

const resolveGateFile = (value: unknown): File | null => {
    if (value instanceof File) {
        return value;
    }

    const payload = value as GateFilePayload | null;
    if (!payload?.__gateFile || !(payload.buffer instanceof ArrayBuffer)) {
        return null;
    }

    return new File(
        [payload.buffer],
        payload.name || "attachment",
        {
            type: payload.type || "application/octet-stream",
            lastModified: payload.lastModified || Date.now(),
        }
    );
};

const normalizeRecordQuery = (query?: any, filters?: any[]) => {
    if (query && typeof query === "object") {
        if (query.type === "logical") {
            return query;
        }

        return {
            type: "logical",
            operator: "and",
            conditions: [query],
        };
    }

    if (Array.isArray(filters) && filters.length > 0) {
        return {
            type: "logical",
            operator: "and",
            conditions: filters
                .filter((filter) => filter && typeof filter === "object" && filter.field)
                .map((filter) => ({
                    type: "condition",
                    field: filter.field,
                    operator: filter.operator || "eq",
                    value: filter.value,
                })),
        };
    }

    return undefined;
};

const applyGateOrderBy = (manager: RecordManagement, orderBy?: any) => {
    if (!orderBy) return;

    const items = Array.isArray(orderBy) ? orderBy : [orderBy];
    items.forEach((item) => {
        if (!item) return;
        if (typeof item === "string") {
            const direction = item.startsWith("-") ? "desc" : "asc";
            const field = item.replace(/^-/, "");
            if (field) manager.orderBy(field, direction);
            return;
        }
        if (typeof item === "object" && item.field) {
            manager.orderBy(item.field, item.direction === "desc" ? "desc" : "asc");
        }
    });
};

const notifyPartialApplication = (value: unknown, context?: GateHostContext) => {
    const partial = (value as any)?.meta?.partial_application;
    if (partial?.message) context?.notify?.(partial.message, "warning");
};

const registry: HostRegistry = {
    "auth.session": async () => {
        const cachedSession = queryClient.getQueryData(queryKeys.session.me());

        if (cachedSession) {
            return (cachedSession as any).data ?? cachedSession;
        }

        return sessionApi.isSessionAlive().then((response) => response.data);
    },
    "auth.login": async (payload?: { email?: string; password?: string }) => {
        return authService.login(payload?.email || "", payload?.password || "");
    },
    "auth.login.remember": async (payload?: { remember?: boolean; email?: string; password?: string }) => {
        if (!payload?.remember) {
            clearRememberedLogin();
            return { ok: true };
        }

        saveRememberedLogin({
            email: payload.email || "",
            password: payload.password || "",
            remember: true,
        });

        return { ok: true };
    },
    "auth.login.remembered": async () => {
        return getRememberedLogin();
    },
    "auth.login_redirect": async (payload?: { returnTo?: string }, context?: GateHostContext) => {
        const loginPage = await fetchLoginGatePage();
        const target = buildPublicLoginPath(loginPage, getSafeReturnTo(payload?.returnTo));

        if (context?.navigate) {
            context.navigate(target, { replace: true });
        }

        return { ok: true, target };
    },
    "auth.logout": async () => authService.logout(),
    "router.navigate": async (payload?: GateNavigatePayload, context?: GateHostContext) => {
        const target = payload?.to;

        if (target && typeof target === "string" && context?.navigate) {
            const shouldPrefixGatePath = payload?.scope === "local"
                && target.startsWith("/")
                && !target.startsWith("/g/")
                && !target.startsWith("/pub/")
                && Boolean(context.basePath);
            const resolvedTarget = shouldPrefixGatePath
                ? `${context.basePath}${target === "/" ? "" : target}`
                : target;

            context.navigate(resolvedTarget, {
                replace: payload?.replace,
                state: payload?.state,
            });
        }

        return { ok: true };
    },
    "ui.notify": async (payload?: { message?: string; level?: string }, context?: GateHostContext) => {
        const message = payload?.message || "";
        const level = payload?.level || "info";

        context?.notify?.(message, level);
        console.info("[Gate notify]", level, message);
        return { ok: true };
    },
    "record.query": async (payload?: { table: string; query?: any; filters?: any[]; orderBy?: any; limit?: number; page?: number; perPage?: number; referenceMode?: ReferenceMode }, context?: GateHostContext) => {
        const manager = new RecordManagement(payload?.table || "");
        manager.translationLanguage(recordLanguage(context));

        const query = normalizeRecordQuery(payload?.query, payload?.filters);

        if (query && (!Array.isArray(query.conditions) || query.conditions.length > 0)) {
            manager.builderFromQuery(query);
        }

        applyGateOrderBy(manager, payload?.orderBy);

        if (payload?.page && payload?.perPage) {
            manager.paginate(payload.page, payload.perPage);
        }

        if (payload?.limit) {
            manager.limit(payload.limit);
        }

        if (payload?.referenceMode) {
            manager.referenceMode(payload.referenceMode);
        }

        return manager.run();
    },
    "record.get": async (payload?: { table: string; id: string }, context?: GateHostContext) =>
        new RecordManagement(payload?.table || "")
            .translationLanguage(recordLanguage(context))
            .get(payload?.id || ""),
    "record.create": async (payload?: { table: string; data: any }, context?: GateHostContext) => {
        const table = payload?.table || "";

        if (table === "platform_packages/import") {
            const response = await axiosInterceptor.post(
                "/api/records/platform_packages/import",
                payload?.data || {},
                await platformPackageOperationRequestConfig()
            );
            return response.data?.data || response.data;
        }

        const record = await new RecordManagement(table).save(payload?.data || {});
        emitRecordMutation({
            table,
            operation: "create",
            ids: (record as any)?.id ? [(record as any).id] : undefined,
            source: "gate",
        });
        notifyPartialApplication(record, context);
        return record;
    },
    "record.update": async (payload?: { table: string; id: string; data: any }, context?: GateHostContext) => {
        const table = payload?.table || "";
        const record = await new RecordManagement(table).update(payload?.id || "", payload?.data || {});
        emitRecordMutation({
            table,
            operation: "update",
            ids: payload?.id ? [payload.id] : undefined,
            source: "gate",
        });
        notifyPartialApplication(record, context);
        return record;
    },
    "record.delete": async (payload?: { table: string; id: string }) => {
        const table = payload?.table || "";
        const response = await recordApi.deleteRecord(table, payload?.id || "");
        emitRecordMutation({
            table,
            operation: "delete",
            ids: payload?.id ? [payload.id] : undefined,
            source: "gate",
        });
        return response.data;
    },
    "email.send": async (payload?: any) => {
        const response = await axiosInterceptor.post("/api/ext/platform/email/send", payload || {});
        return response.data?.data || response.data;
    },
    "email.send_template": async (payload?: any) => {
        const response = await axiosInterceptor.post("/api/ext/platform/email/send-template", payload || {});
        return response.data?.data || response.data;
    },
    "email.render_template": async (payload?: any) => {
        const response = await axiosInterceptor.post("/api/ext/platform/email/render-template", payload || {});
        return response.data?.data || response.data;
    },
    "email.upload_attachment": async (payload?: { messageId?: string; file?: File | GateFilePayload }) => {
        if (!payload?.file) {
            throw new Error("File is required");
        }

        const file = resolveGateFile(payload.file);
        if (!file) {
            throw new Error("Gate email attachment payload must include a valid file");
        }

        let messageId = payload.messageId;
        let message: any = null;

        if (!messageId) {
            const response = await recordApi.postRecord("sys_email_messages", {
                direction: "outbound",
                state: "draft",
                subject: "",
            } as any);
            message = (response.data as any)?.data || response.data;
            messageId = message?.id;
        }

        if (!messageId) {
            throw new Error("Unable to create email draft");
        }

        const attachment = await attachmentApi.uploadAttachment("sys_email_messages", messageId, file);
        return { messageId, message, attachment };
    },
    "email.delete_attachment": async (payload?: { messageId?: string; attachmentId?: string }) => {
        if (!payload?.messageId || !payload?.attachmentId) {
            throw new Error("Message and attachment are required");
        }

        return attachmentApi.deleteAttachment("sys_email_messages", payload.messageId, payload.attachmentId);
    },
    "attachment.list": async (payload?: { table?: string; recordId?: string; draftToken?: string; column?: string | null }) => {
        if (!payload?.table || (!payload.recordId && !payload.draftToken)) {
            throw new Error("Table and record or draft token are required");
        }

        return payload.draftToken
            ? attachmentApi.listDraftAttachments(payload.table, payload.draftToken, payload.column)
            : attachmentApi.listAttachments(payload.table, payload.recordId || "", payload.column);
    },
    "attachment.upload": async (payload?: { table?: string; recordId?: string; draftToken?: string; column?: string | null; file?: File | GateFilePayload }) => {
        if (!payload?.table || (!payload.recordId && !payload.draftToken) || !payload.file) {
            throw new Error("Table, record or draft token, and file are required");
        }

        const file = resolveGateFile(payload.file);
        if (!file) {
            throw new Error("Gate attachment payload must include a valid file");
        }

        return payload.draftToken
            ? attachmentApi.uploadDraftAttachment(payload.table, payload.draftToken, file, payload.column)
            : attachmentApi.uploadAttachment(payload.table, payload.recordId || "", file, payload.column);
    },
    "attachment.delete": async (payload?: { table?: string; recordId?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        if (!payload?.table || (!payload.recordId && !payload.draftToken) || !payload.attachmentId) {
            throw new Error("Table, record or draft token, and attachment are required");
        }

        return payload.draftToken
            ? attachmentApi.deleteDraftAttachment(payload.table, payload.draftToken, payload.attachmentId, payload.column)
            : attachmentApi.deleteAttachment(payload.table, payload.recordId || "", payload.attachmentId, payload.column);
    },
    "attachment.download_url": async (payload?: { table?: string; recordId?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        if (!payload?.table || (!payload.recordId && !payload.draftToken) || !payload.attachmentId) {
            throw new Error("Table, record or draft token, and attachment are required");
        }

        return payload.draftToken
            ? attachmentApi.downloadDraftAttachmentUrl(payload.table, payload.draftToken, payload.attachmentId, payload.column)
            : attachmentApi.downloadAttachmentUrl(payload.table, payload.recordId || "", payload.attachmentId, payload.column);
    },
    "attachment.view_url": async (payload?: { table?: string; recordId?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        if (!payload?.table || (!payload.recordId && !payload.draftToken) || !payload.attachmentId) {
            throw new Error("Table, record or draft token, and attachment are required");
        }

        return payload.draftToken
            ? attachmentApi.viewDraftAttachmentUrl(payload.table, payload.draftToken, payload.attachmentId, payload.column)
            : attachmentApi.viewAttachmentUrl(payload.table, payload.recordId || "", payload.attachmentId, payload.column);
    },
    "translations.coverage": async (payload?: Record<string, any>) => {
        const response = await axiosInterceptor.get("/api/translations/coverage", { params: payload });
        // Return the full envelope ({ data, meta, ... }), not just `.data` — the
        // widget needs `meta.total_keys`/`meta.missing_by_language`/`meta.page` for
        // pagination and the summary chips, same convention as "record.query"
        // (which returns RecordManagement#run()'s full RecordsResponse).
        return response.data;
    },
    "service_portal.submit": async (payload?: { formId?: string; id?: string; record?: any; data?: any; targetTable?: string; attachmentDraftToken?: string; requestSnapshot?: any }) => {
        const formId = payload?.formId || payload?.id || "";
        if (!formId) {
            throw new Error("Service Portal form is required");
        }

        const response = await axiosInterceptor.post(`/api/service_portal/forms/${formId}/submit`, {
            record: payload?.record || payload?.data || {},
            target_table: payload?.targetTable,
            attachment_draft_token: payload?.attachmentDraftToken,
            request_snapshot: payload?.requestSnapshot,
        });
        const result = response.data?.data || response.data;
        const targetTable = result?.target_table || payload?.targetTable;
        const targetRecordId = result?.target_record_id;
        const requestId = result?.request_id;

        if (targetTable) {
            emitRecordMutation({
                table: targetTable,
                operation: "create",
                ids: targetRecordId ? [targetRecordId] : undefined,
                source: "gate",
            });
        }

        emitRecordMutation({
            table: "sys_sp_requests",
            operation: "create",
            ids: requestId ? [requestId] : undefined,
            source: "gate",
        });

        return result;
    },
    "service_portal.form.get": async (payload?: { formId?: string; id?: string }) => {
        const formId = payload?.formId || payload?.id || "";
        if (!formId) throw new Error("Service Portal form is required");
        const response = await axiosInterceptor.get(`/api/service_portal/forms/${encodeURIComponent(formId)}`);
        return response.data?.data || response.data;
    },
    "service_portal.invite.get": async (payload?: { token?: string }) => {
        if (!payload?.token) throw new Error("Invitation token is required");
        const response = await axiosInterceptor.get(`/api/service_portal/invites/${encodeURIComponent(payload.token)}`);
        return response.data?.data || response.data;
    },
    "service_portal.invite.submit": async (payload?: { token?: string; record?: any; attachmentDraftToken?: string }) => {
        if (!payload?.token) throw new Error("Invitation token is required");
        const response = await axiosInterceptor.post(`/api/service_portal/invites/${encodeURIComponent(payload.token)}/submit`, {
            record: payload.record || {}, attachment_draft_token: payload.attachmentDraftToken,
        });
        const result = response.data?.data || response.data;
        if (result?.target_table) emitRecordMutation({ table: result.target_table, operation: "update", ids: [result.target_record_id], source: "gate" });
        return result;
    },
    "service_portal.request.public_get": async (payload?: { requestId?: string; id?: string }) => {
        const requestId = payload?.requestId || payload?.id || "";
        if (!requestId) {
            throw new Error("Service Portal request is required");
        }

        const response = await axiosInterceptor.get(`/api/service_portal/requests/${requestId}/public`);
        return response.data?.data || response.data;
    },
    "service_portal.reference.lookup": async (payload?: { formId?: string; column?: string; q?: string }) => {
        if (!payload?.formId || !payload.column) throw new Error("Service Portal form and reference field are required");
        const response = await axiosInterceptor.get(`/api/service_portal/forms/${encodeURIComponent(payload.formId)}/fields/${encodeURIComponent(payload.column)}/lookup`,
            { params: { q: payload.q || "" } });
        return response.data?.data || response.data;
    },
    "service_portal.attachment.list": async (payload?: { formId?: string; id?: string; draftToken?: string; column?: string | null }) => {
        const formId = payload?.formId || payload?.id || "";
        const column = payload?.column || "";
        if (!formId || !payload?.draftToken || !column) {
            throw new Error("Service Portal form, draft token, and attachment field are required");
        }

        return attachmentApi.listServicePortalDraftAttachments(formId, payload.draftToken, column);
    },
    "service_portal.attachment.upload": async (payload?: { formId?: string; id?: string; draftToken?: string; column?: string | null; file?: File | GateFilePayload }) => {
        const formId = payload?.formId || payload?.id || "";
        const column = payload?.column || "";
        if (!formId || !payload?.draftToken || !column || !payload.file) {
            throw new Error("Service Portal form, draft token, attachment field, and file are required");
        }

        const file = resolveGateFile(payload.file);
        if (!file) {
            throw new Error("Gate attachment payload must include a valid file");
        }

        return attachmentApi.uploadServicePortalDraftAttachment(formId, payload.draftToken, file, column);
    },
    "service_portal.attachment.delete": async (payload?: { formId?: string; id?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        const formId = payload?.formId || payload?.id || "";
        const column = payload?.column || "";
        if (!formId || !payload?.draftToken || !payload.attachmentId || !column) {
            throw new Error("Service Portal form, draft token, attachment, and field are required");
        }

        return attachmentApi.deleteServicePortalDraftAttachment(formId, payload.draftToken, payload.attachmentId, column);
    },
    "service_portal.attachment.download_url": async (payload?: { formId?: string; id?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        const formId = payload?.formId || payload?.id || "";
        const column = payload?.column || "";
        if (!formId || !payload?.draftToken || !payload.attachmentId || !column) {
            throw new Error("Service Portal form, draft token, attachment, and field are required");
        }

        return attachmentApi.downloadServicePortalDraftAttachmentUrl(formId, payload.draftToken, payload.attachmentId, column);
    },
    "service_portal.attachment.view_url": async (payload?: { formId?: string; id?: string; draftToken?: string; attachmentId?: string; column?: string | null }) => {
        const formId = payload?.formId || payload?.id || "";
        const column = payload?.column || "";
        if (!formId || !payload?.draftToken || !payload.attachmentId || !column) {
            throw new Error("Service Portal form, draft token, attachment, and field are required");
        }

        return attachmentApi.viewServicePortalDraftAttachmentUrl(formId, payload.draftToken, payload.attachmentId, column);
    },
    "session.user.create": async (payload?: any) => sessionApi.postUser(payload).then((response) => response.data),
    "session.user.update": async (payload?: any) => sessionApi.updateUser(payload).then((response) => response.data),
    "session.user.password": async (payload?: { userId: string; data: any }) => sessionApi.updateUserPassword(payload?.userId || "", payload?.data).then((response) => response.data),
};

export const isGateMethodAllowed = (allowedMethods: GateApiScope[], method: string): method is GateApiScope => {
    return allowedMethods.includes(method as GateApiScope);
};

export const invokeGateHostMethod = async (method: GateApiScope, payload?: unknown) => {
    return invokeGateHostMethodWithContext(method, payload);
};

export const invokeGateHostMethodWithContext = async (method: GateApiScope, payload?: unknown, context?: GateHostContext) => {
    const handler = registry[method];

    if (!handler) {
        throw new Error(`Gate method not implemented: ${method}`);
    }

    return handler(payload, context);
};
